import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { View } from 'react-native';

interface PublishParams {
    url: string;
    fps: number;
    idealWidth: number;
    maxWidth: number;
}

interface ConsumeParams {
    url: string;
}

interface MethodReturn {
    sessionId: string;
    simulator: string;
}

export type ConnectionState = 'closed' | 'connected' | 'connecting' | 'disconnected' | 'failed' | 'new';

class SrsRtcWhipWhep {
    public readonly pc: RTCPeerConnection;
    public readonly stream: MediaStream;

    onConnectionStateChange?: (state: ConnectionState) => void;

    private parseId(url: string, offer: string | undefined, answer: string): MethodReturn {

        if (offer === undefined) {
            throw new Error('Invalid Offer');
        }

        let sessionId = offer.substr(offer.indexOf('a=ice-ufrag:') + 'a=ice-ufrag:'.length);
        sessionId = sessionId.substr(0, sessionId.indexOf('\n') - 1) + ':';
        sessionId += answer.substr(answer.indexOf('a=ice-ufrag:') + 'a=ice-ufrag:'.length);
        sessionId = sessionId.substr(0, sessionId.indexOf('\n'));

        const a = new URL(url);

        return {
            sessionId: sessionId,
            simulator: a.protocol + '//' + a.host + '/rtc/v1/nack/',
        };
    }

    constructor() {
        this.pc = new RTCPeerConnection(undefined);

        // To keep api consistent between player and publisher.
        // @see https://developer.mozilla.org/en-US/docs/Web/API/RTCPeerConnection/addStream#Migrating_to_addTrack
        // @see https://webrtc.org/getting-started/media-devices
        this.stream = new MediaStream();

        // https://developer.mozilla.org/en-US/docs/Web/API/RTCPeerConnection/ontrack
        this.pc.ontrack = (event) => {
            if (this.ontrack) {
                this.ontrack(event);
            }
        };

        this.pc.onconnectionstatechange = () => {
            const state = this.pc.connectionState as ConnectionState;
            this.onConnectionStateChange && this.onConnectionStateChange(state);
        };

    }

    async publish(params: PublishParams) {

        const constraints = {
            audio: false,
            video: {
                width: { ideal: params.idealWidth, max: params.maxWidth }
            }
        };

        this.pc.addTransceiver('video', { direction: 'sendonly' });

        const userStream = await navigator.mediaDevices.getUserMedia(constraints);

        for (const track of userStream.getTracks()) {
            await track.applyConstraints({
                frameRate: { max: params.fps, min: params.fps, exact: params.fps, ideal: params.fps }
            });

            this.pc.addTrack(track);
            // Notify about local track when stream is ok.
            this.ontrack && this.ontrack({ track: track });
        }

        const offer = await this.pc.createOffer();
        await this.pc.setLocalDescription(offer);

        const answer = await new Promise<string>((resolve, reject) => {
            console.log(`Generated offer: ${offer.sdp}`);

            const xhr = new XMLHttpRequest();
            xhr.onload = () => {
                if (xhr.readyState !== xhr.DONE) {
                    return;
                }
                if (xhr.status !== 200 && xhr.status !== 201) {
                    return reject(xhr);
                }
                const data = xhr.responseText;
                console.log('Got answer: ', data);
                return resolve(data);
            };
            xhr.open('POST', params.url, true);
            xhr.setRequestHeader('Content-type', 'application/sdp');
            xhr.send(offer.sdp);
        });

        await this.pc.setRemoteDescription(
            new RTCSessionDescription({ type: 'answer', sdp: answer })
        );

        return this.parseId(params.url, offer.sdp, answer);
    }

    async play(params: ConsumeParams) {
        if (params.url.indexOf('/whip-play/') === -1 && params.url.indexOf('/whep/') === -1) {
            throw new Error(`invalid WHEP url ${params.url}`);
        }

        this.pc.addTransceiver('video', { direction: 'recvonly' });

        const offer = await this.pc.createOffer();
        await this.pc.setLocalDescription(offer);
        const answer = await new Promise<string>((resolve, reject) => {
            console.log(`Generated offer: ${offer.sdp}`);

            const xhr = new XMLHttpRequest();
            xhr.onload = () => {
                if (xhr.readyState !== xhr.DONE) {
                    return;
                }
                if (xhr.status !== 200 && xhr.status !== 201) {
                    return reject(xhr);
                }
                const data = xhr.responseText;
                console.log('Got answer: ', data);
                return resolve(data);
            };
            xhr.open('POST', params.url, true);
            xhr.setRequestHeader('Content-type', 'application/sdp');
            xhr.send(offer.sdp);
        });
        await this.pc.setRemoteDescription(
            new RTCSessionDescription({ type: 'answer', sdp: answer })
        );

        return this.parseId(params.url, offer.sdp, answer);

    }

    async close() {
        try {
            console.log('[WebRTC] closing connection…');

            // Para todos os transceivers
            if (this.pc.getTransceivers) {
                for (const t of this.pc.getTransceivers()) {
                    try {
                        t.stop && t.stop();
                    } catch {
                        // pass
                    }
                }
            }

            // Para todos os senders e seus tracks
            for (const sender of this.pc.getSenders()) {
                try {
                    const track = sender.track;
                    if (track) {
                        track.stop();
                    }
                } catch {
                    // pass
                }

                try {
                    this.pc.removeTrack(sender);
                } catch {
                    // pass
                }
            }

            // Para todos os receivers
            for (const receiver of this.pc.getReceivers()) {
                try {
                    const track = receiver.track;
                    if (track) {
                        track.stop();
                    }
                } catch {
                    // pass
                }
            }

            // Limpa o MediaStream
            try {
                this.stream.getTracks().forEach(t => t.stop());
            } catch {
                // pass
            }

            // Fecha a PC
            try {
                this.pc.close();
            } catch (err) {
                // pass
            }

            // Remove handlers
            this.pc.ontrack = null;
            this.pc.onicecandidate = null;
            this.pc.onconnectionstatechange = null;
            this.pc.onicegatheringstatechange = null;
            this.pc.oniceconnectionstatechange = null;
            this.pc.onsignalingstatechange = null;
            this.ontrack = () => { };

            await new Promise((resolve) => setTimeout(resolve, 5000));

        } catch (e) {
            console.error('[WebRTC] close() error', e);
        }

    }

    ontrack(event: { track: MediaStreamTrack; }) {
        // Add track to stream of SDK.
        this.stream.addTrack(event.track);
    }

}

const RtcMediaPlayer = forwardRef((props: {
    setConnectionState?: React.Dispatch<React.SetStateAction<ConnectionState | undefined>>;
}, ref) => {

    const videoRef = React.createRef<HTMLVideoElement>();
    const sdkRef = useRef<SrsRtcWhipWhep>();
    const lastPublishParamsRef = useRef<PublishParams>();
    const lastConsumeParamsRef = useRef<ConsumeParams>();
    const reconnectingRef = useRef(false);
    const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
    const [isLoading, setIsLoading] = useState(false);

    async function initSdkForPreview(video: HTMLVideoElement) {

        // fecha conexão anterior se existir
        if (sdkRef.current) {
            await sdkRef.current.close();
            sdkRef.current = undefined;
        }

        const sdk = new SrsRtcWhipWhep();

        // preview local/remote
        video.srcObject = sdk.stream;

        // callback de estado de conexão → aqui fazemos retry
        sdk.onConnectionStateChange = (state) => {
            console.log('[WebRTC] connectionState =', state);

            props.setConnectionState && props.setConnectionState(state);

            if (state === 'disconnected' || state === 'failed') {
                if (reconnectingRef.current) {
                    return;
                }
                reconnectingRef.current = true;

                reconnectTimeoutRef.current = setTimeout(async () => {
                    reconnectingRef.current = false;

                    if (lastPublishParamsRef.current) {
                        try {
                            console.log('[WebRTC] retrying publish...');
                            await publish(lastPublishParamsRef.current);
                        } catch (e) {
                            console.error('[WebRTC] retry failed', e);
                        }
                    }

                    if (lastConsumeParamsRef.current) {
                        try {
                            console.log('[WebRTC] retrying consume...');
                            await consume(lastConsumeParamsRef.current);
                        } catch (e) {
                            console.error('[WebRTC] retry failed', e);
                        }
                    }

                }, 2500);
            }
        };

        sdkRef.current = sdk;
        return sdk;
    }

    async function consume(params: ConsumeParams) {
        lastConsumeParamsRef.current = params;

        const video = videoRef.current;

        if (!video) {
            return;
        }

        const sdk = await initSdkForPreview(video);

        function monitorVideoFlow(
            pc: RTCPeerConnection,
            {
                intervalMs = 1000,
                freezeThreshold = 2,
                onFreeze,
                onResume,
            }: {
                intervalMs?: number;
                freezeThreshold?: number;
                onFreeze: () => void;
                onResume: () => void;
            }
        ) {
            let lastFrames = 0;
            let frozenCount = 0;
            let frozen = true;
            setIsLoading(true);

            const timer = setInterval(async () => {
                const stats = await pc.getStats();

                stats.forEach(report => {
                    if (
                        report.type === 'inbound-rtp' &&
                        report.kind === 'video'
                    ) {
                        if (report.framesDecoded === lastFrames) {
                            frozenCount++;

                            if (!frozen && frozenCount >= freezeThreshold) {
                                frozen = true;
                                onFreeze();
                            }
                        } else {
                            lastFrames = report.framesDecoded;
                            frozenCount = 0;

                            if (frozen) {
                                frozen = false;
                                onResume();
                            }
                        }
                    }
                });
            }, intervalMs);

            return () => clearInterval(timer);
        }

        monitorVideoFlow(sdk.pc, {
            onFreeze: () => setIsLoading(true),
            onResume: () => setIsLoading(false),
        });

        return await sdk.play(params);
    }

    async function publish(params: PublishParams) {
        lastPublishParamsRef.current = params;

        const video = videoRef.current;

        if (!video) {
            return;
        }

        const sdk = await initSdkForPreview(video);

        return await sdk.publish(params);
    }

    useImperativeHandle(ref, () => ({
        publish,
        consume,
    }));

    useEffect(() => {

        const video = videoRef.current;

        if (!video) {
            return;
        }

    }, [videoRef]);

    return <>
        <View style={{ width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center', zIndex: 2, backgroundColor: 'black' }}>
            <video
                ref={videoRef}
                autoPlay={true}
                controls={false}
                disablePictureInPicture={true}
                muted={true}
                style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                }}
            />
        </View>
        <View style={{ position: 'absolute', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center', zIndex: isLoading ? 10 : 1 }}>
            <View style={{ width: 50, height: 50 }}>
                <FontAwesomeIcon size='2x' spin={true} icon={faSpinner} color='#fff' />
            </View>
        </View>

    </>;
});

export type RtcMediaPlayerRef = {
    publish: (params: { url: string; }) => Promise<MethodReturn>;
    consume: (params: { url: string; }) => Promise<MethodReturn>;
};

RtcMediaPlayer.displayName = 'RtcMediaPlayer';

export default RtcMediaPlayer;
